import { type ReactNode, useId, useLayoutEffect, useRef } from "react";
import { cn } from "../lib/utils";
import {
  ATTACHE_BASE_MODULAIRE,
  COULEURS_MAX,
  HAUTEUR_MAX_MM,
  HAUTEUR_MIN_MM,
  NOM_MOTIF,
  type Rapport,
  type Reglages,
  SOL_HAUTEUR_MAX_MM,
  SOL_HAUTEUR_MIN_MM,
} from "./contract";
import { largeurMinDesPastilles, nombreFr } from "./silhouettes-textes";

/**
 * La carte Reglages du module Silhouettes : les six reglages qui changent quelque chose au
 * quotidien (spec, section 1), en controles faits main, chacun nomme par son libelle. Les
 * reglages avances de la ligne de commande restent hors de la page. Depuis #397, une septieme
 * ligne, « Traits de couleur », n'apparait que quand le rendu a des traits de couleur a epaissir.
 *
 * La saisie garde ce qui est tape tel quel (un nom invalide, une hauteur vide) : c'est
 * `lireSaisie` qui dit si elle forme des reglages valides, et rien ne part tant qu'elle n'en
 * forme pas.
 */

export type Saisie = {
  nom: string;
  /** Le texte du champ, pas encore un nombre. */
  hauteur: string;
  attache: boolean;
  couleursGardees: boolean;
  nombreCouleurs: "auto" | number;
  sol: Reglages["sol"];
  /** Le texte du champ, vide quand c'est l'outil qui decide de la hauteur de la ligne. */
  solHauteur: string;
  mode: Reglages["mode"];
  /**
   * Les couleurs dont on epaissit les traits trop fins (#397), par leur nom dans le rapport. Les
   * noms d'une image ne valent pas pour une autre : la liste repart vide a chaque nouvelle image.
   * Elle reste dans la saisie quand les couleurs ne sont plus gardees, et revient avec elles.
   */
  couleursEpaissies: string[];
};

/** La saisie de depart : les reglages par defaut du backend, et un nom vide. */
export function saisieDepuis(reglages: Reglages): Saisie {
  return {
    // Vide jusqu'a la premiere image : le champ affiche « pris du nom de l'image ».
    nom: "",
    hauteur: String(reglages.hauteur_mm),
    attache: reglages.attache !== null,
    couleursGardees: reglages.couleurs !== null,
    nombreCouleurs: reglages.couleurs ?? "auto",
    sol: reglages.sol,
    // Vide : `auto`, l'outil releve la ligne juste assez pour poser les deux pieds.
    solHauteur: reglages.sol_hauteur_mm == null ? "" : String(reglages.sol_hauteur_mm),
    mode: reglages.mode,
    // Vide : rien n'est epaissi tant que Nicolas ne l'a pas demande, couleur par couleur.
    couleursEpaissies: [...(reglages.couleurs_epaissies ?? [])],
  };
}

export type Lecture = {
  reglages: Reglages;
  valides: boolean;
  nomInvalide: boolean;
  hauteurInvalide: boolean;
  solHauteurInvalide: boolean;
};

/**
 * Vrai quand la hauteur de la ligne de sol a un sens : une ligne de sol ne sert qu'a poser la
 * piece sur la barre d'une attache, et « Ligne de sol : Non » la refuse tout net. Hors de la,
 * le champ ne part pas et ce qu'il porte n'empeche rien : l'outil dirait « sans effet ».
 */
function solHauteurUtile(saisie: Saisie): boolean {
  return saisie.attache && saisie.sol !== "non";
}

/** Les reglages que la saisie donne, et ce qui l'empeche de partir. */
export function lireSaisie(saisie: Saisie): Lecture {
  const hauteur = Number(saisie.hauteur.replace(",", "."));
  const hauteurInvalide =
    saisie.hauteur.trim() === "" ||
    !Number.isFinite(hauteur) ||
    hauteur < HAUTEUR_MIN_MM ||
    hauteur > HAUTEUR_MAX_MM;
  const nomInvalide = !NOM_MOTIF.test(saisie.nom);
  const utile = solHauteurUtile(saisie);
  const solHauteurTexte = saisie.solHauteur.trim();
  const solHauteur = Number(solHauteurTexte.replace(",", "."));
  const solHauteurInvalide =
    utile &&
    solHauteurTexte !== "" &&
    (!Number.isFinite(solHauteur) ||
      solHauteur < SOL_HAUTEUR_MIN_MM ||
      solHauteur > SOL_HAUTEUR_MAX_MM);
  const reglages: Reglages = {
    nom: saisie.nom,
    hauteur_mm: hauteurInvalide ? 0 : hauteur,
    attache: saisie.attache ? ATTACHE_BASE_MODULAIRE : null,
    couleurs: saisie.couleursGardees ? saisie.nombreCouleurs : null,
    // Sans attache, la ligne de sol n'existe pas : l'outil dirait « sans effet ».
    sol: saisie.attache ? saisie.sol : "auto",
    mode: saisie.mode,
  };
  // La cle n'est posee que pour une hauteur imposee et utile : absente, c'est l'outil qui
  // decide. Elle n'est donc ni envoyee sur « Non », ni sans attache, ni quand le champ est
  // vide (`JSON.stringify` laisserait passer `null`, qui vaut `auto` mais dit autre chose).
  if (utile && solHauteurTexte !== "" && !solHauteurInvalide) {
    reglages.sol_hauteur_mm = solHauteur;
  }
  // Meme regle pour les couleurs a epaissir (#397) : la cle n'est posee que pour des noms, et
  // seulement si les couleurs sont gardees. Sans pastille, il n'y a rien a epaissir, et l'outil
  // dirait « sans effet ». Absente, c'est le comportement d'avant : les traits trop fins sont jetes.
  if (saisie.couleursGardees && saisie.couleursEpaissies.length > 0) {
    reglages.couleurs_epaissies = [...saisie.couleursEpaissies];
  }
  return {
    reglages,
    valides: !nomInvalide && !hauteurInvalide && !solHauteurInvalide,
    nomInvalide,
    hauteurInvalide,
    solHauteurInvalide,
  };
}

/** Un reglage cite par un avertissement de l'outil, applique a la saisie. */
export function appliquer(saisie: Saisie, reglage: Partial<Reglages>): Saisie {
  const suivante = { ...saisie };
  if (reglage.nom !== undefined) suivante.nom = reglage.nom;
  if (reglage.hauteur_mm !== undefined) suivante.hauteur = String(reglage.hauteur_mm);
  if (reglage.attache !== undefined) suivante.attache = reglage.attache !== null;
  if (reglage.couleurs !== undefined) {
    suivante.couleursGardees = reglage.couleurs !== null;
    if (reglage.couleurs !== null) suivante.nombreCouleurs = reglage.couleurs;
  }
  if (reglage.sol !== undefined) suivante.sol = reglage.sol;
  if (reglage.mode !== undefined) suivante.mode = reglage.mode;
  // Des noms de couleur s'ajoutent a la liste, ils ne la remplacent pas (#397) : « épaissir blanc »,
  // cite par un avertissement, ne doit pas defaire ce qui est deja coche pour orange.
  if (reglage.couleurs_epaissies != null) {
    suivante.couleursEpaissies = [
      ...new Set([...saisie.couleursEpaissies, ...reglage.couleurs_epaissies]),
    ];
  }
  return suivante;
}

/** Vrai quand la saisie porte deja ce reglage : le lien qui l'appliquerait n'a pas lieu d'etre. */
export function dejaApplique(saisie: Saisie, reglage: Partial<Reglages>): boolean {
  const actuels = lireSaisie(saisie).reglages;
  return (Object.keys(reglage) as (keyof Reglages)[]).every((cle) => {
    // Une liste (#397) ne se compare pas par `===` : un lien qui ajoute des noms n'a plus lieu
    // d'etre quand tous les siens sont deja dans la liste. Lue dans la saisie et non dans ce qui part,
    // car sans couleurs gardees la cle ne part pas, et le lien d'un message perime resterait vivant
    // sans rien faire.
    if (cle === "couleurs_epaissies") {
      return (reglage.couleurs_epaissies ?? []).every((nom) =>
        saisie.couleursEpaissies.includes(nom),
      );
    }
    return reglage[cle] === actuels[cle];
  });
}

/** Une couleur de la ligne « Traits de couleur » : son nom, sa teinte si on l'a, et si elle est epaissie. */
export type CouleurConcernee = { nom: string; hexa: string | null; epaissie: boolean };

/** Un hexadecimal de six chiffres : rien ne passe dans un style sans l'etre (comme l'etagere). */
const HEXADECIMAL = /^#[0-9A-Fa-f]{6}$/;

function hexaSur(valeur: string | undefined): string | null {
  return valeur !== undefined && HEXADECIMAL.test(valeur) ? valeur : null;
}

/**
 * Les couleurs de la ligne « Traits de couleur » (#397), dans l'ordre du rapport, puis celles que
 * la saisie a deja et que le rapport ne cite plus. Aucune quand les couleurs ne sont pas gardees :
 * sans pastille, il n'y a pas de trait de couleur. Une couleur dont tous les traits sont fins n'a
 * plus de pastille et manque a `rapport.couleurs`, mais le rapport la garde dans ses traits : c'est
 * par la que la page peut la proposer. La teinte vient des traits, sinon de la couleur du rapport.
 */
export function couleursConcernees(saisie: Saisie, rapport: Rapport | null): CouleurConcernee[] {
  if (!saisie.couleursGardees) return [];
  const traits = rapport?.traits_de_couleur ?? [];
  const noms = new Set([...traits.map((trait) => trait.nom), ...saisie.couleursEpaissies]);
  return [...noms].map((nom) => ({
    nom,
    hexa:
      hexaSur(traits.find((trait) => trait.nom === nom)?.hexa) ??
      hexaSur(rapport?.couleurs?.find((couleur) => couleur.nom === nom)?.hexa),
    epaissie: saisie.couleursEpaissies.includes(nom),
  }));
}

/** Le champ de saisie de la maquette. `CHAMP`, `Ligne` et `Bascule` servent aussi aux cadres (#167). */
export const CHAMP =
  "h-9 rounded-md border border-input bg-card px-2.5 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50";

/** Une ligne de la carte : le libelle a gauche, le controle, puis son aide en dessous. */
export function Ligne({
  libelle,
  pour,
  idLibelle,
  aide,
  aideId,
  erreur,
  children,
}: {
  libelle: string;
  /** L'identifiant du champ que le libelle nomme, quand c'est un champ. */
  pour?: string;
  /** L'identifiant du libelle, quand il nomme un groupe (`aria-labelledby`). */
  idLibelle?: string;
  aide?: string | null;
  aideId?: string;
  erreur?: string | null;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[110px_minmax(0,1fr)] items-center gap-x-2.5 gap-y-1 border-t border-border py-2 first:border-t-0">
      {pour ? (
        <label className="text-sm" htmlFor={pour}>
          {libelle}
        </label>
      ) : (
        <span className="text-sm" id={idLibelle}>
          {libelle}
        </span>
      )}
      <div className="min-w-0">{children}</div>
      {erreur ? (
        <p className="col-start-2 text-[13px] text-destructive" id={aideId}>
          {erreur}
        </p>
      ) : aide ? (
        <p className="col-start-2 text-[13px] text-muted-foreground" id={aideId}>
          {aide}
        </p>
      ) : null}
    </div>
  );
}

export type Option<T extends string> = { valeur: T; libelle: string };

/**
 * Une bascule : un groupe de radios natifs, dessines en boutons accoles. Le navigateur fait
 * le reste : Tab entre sur le choix courant, les fleches changent de choix. Des radios
 * natifs plutot que des boutons `role="radio"` : la regle a11y de Biome le demande, et le
 * clavier vient sans une ligne de code. Le contour de focus est pose sur l'etiquette, le
 * radio lui-meme etant cache.
 *
 * `etale` : sous `md`, la bascule prend toute la largeur et chaque choix la moitie (le choix du mode
 * de la page, maquette `SilhouettesSerreLivresMobile.dc.html`) ; a partir de `md`, elle reprend sa
 * forme ordinaire.
 */
export function Bascule<T extends string>({
  idLibelle,
  options,
  valeur,
  changer,
  desactivee = false,
  decritPar,
  etale = false,
}: {
  idLibelle: string;
  options: readonly Option<T>[];
  valeur: T;
  changer: (valeur: T) => void;
  desactivee?: boolean;
  decritPar?: string;
  etale?: boolean;
}) {
  return (
    <div
      aria-describedby={decritPar}
      aria-disabled={desactivee || undefined}
      aria-labelledby={idLibelle}
      className={cn(
        "max-w-full flex-wrap",
        etale ? "flex w-full md:inline-flex md:w-auto" : "inline-flex",
      )}
      role="radiogroup"
    >
      {options.map((option) => {
        const choisi = option.valeur === valeur;
        return (
          <label
            className={cn(
              "-ml-px inline-flex items-center border border-input px-3 text-[14px] first:ml-0 first:rounded-l-md last:rounded-r-md",
              // La forme etalee est celle du telephone : 44 px de haut pour le pouce.
              etale ? "h-11 flex-1 justify-center md:h-9 md:flex-none" : "h-9",
              "has-[:focus-visible]:relative has-[:focus-visible]:z-10 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-card",
              desactivee ? "cursor-not-allowed opacity-50" : "cursor-pointer",
              choisi
                ? "relative z-[1] border-primary bg-primary text-primary-foreground"
                : cn("bg-card text-foreground", !desactivee && "hover:bg-muted"),
            )}
            key={option.valeur}
          >
            <input
              checked={choisi}
              className="sr-only"
              disabled={desactivee}
              name={idLibelle}
              onChange={() => changer(option.valeur)}
              type="radio"
              value={option.valeur}
            />
            {option.libelle}
          </label>
        );
      })}
    </div>
  );
}

const ATTACHES: readonly Option<"oui" | "non">[] = [
  { valeur: "oui", libelle: "Base modulaire" },
  { valeur: "non", libelle: "Aucune" },
];
const SOLS: readonly Option<Reglages["sol"]>[] = [
  { valeur: "auto", libelle: "Auto" },
  { valeur: "oui", libelle: "Oui" },
  { valeur: "non", libelle: "Non" },
];
const MODES: readonly Option<Reglages["mode"]>[] = [
  { valeur: "fidele", libelle: "Fidèle" },
  { valeur: "epaissir", libelle: "Épaissir" },
];
const NOMBRES = ["auto", ...Array.from({ length: COULEURS_MAX }, (_, i) => String(i + 1))];

/**
 * La carte Reglages dans la colonne d'une figurine du mode serre-livres (#337). Elle perd sa carte
 * et son titre (un titre cache, pour le lecteur d'ecran, les remplace), garde tous ses reglages, et
 * change quatre choses : la ligne Nom est celle de la page (le nom qu'on affiche, pas un nom de
 * fichier), l'attache est verrouillee sur la base modulaire, le miroir suit la hauteur, et tout ce
 * qui vient apres (Attache, Couleurs, Ligne de sol, Hauteur du sol, Traits fins) est plie sous
 * « Réglages », ferme, avec son resume dans la ligne du pli (decision de Nicolas, le 5 octobre 2026).
 */
export type ReglagesSerreLivres = {
  /** La ligne du nom : sa saisie, son aide, son signalement. */
  ligneNom: ReactNode;
  /** Ce qui suit la hauteur, avant le pli : l'interrupteur Miroir. */
  suite: ReactNode;
  /** Le resume des reglages plies, en une ligne (`resumeDesReglages`) : la ligne du pli le montre ferme. */
  resume: string;
  /**
   * Le « hors tout » de la hauteur tapee (`legendeHorsTout`), sur la ligne du champ comme la planche :
   * « 97 hors tout » apres « mm ». Absent quand la hauteur est refusee : son refus le remplace.
   */
  horsTout: string | null;
  /** Le titre cache : « Reglages de la figurine de gauche ». */
  titre: string;
};

/**
 * Le pli « Réglages » d'une colonne du serre-livres : ferme, sa ligne dit ce qu'il cache (et ce qui
 * refuse sa valeur). Un `details` natif : le clavier, le lecteur d'ecran et le repli viennent avec.
 */
function ReglagesPlies({
  resume,
  erreur,
  children,
}: {
  resume: string;
  erreur: string | null;
  children: ReactNode;
}) {
  return (
    <details className="group mt-1 rounded-md border border-border" data-reglages="">
      <summary className="min-h-11 cursor-pointer rounded-md px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:min-h-9">
        <span className="font-medium">Réglages</span>
        <span className="block text-[13px] text-muted-foreground">{resume}</span>
        {erreur ? <span className="block text-[13px] text-destructive">{erreur}</span> : null}
      </summary>
      <div className="border-t border-border px-3">{children}</div>
    </details>
  );
}

/**
 * La ligne « Traits de couleur » (#397), le pendant de « Traits fins » pour les pastilles : « Traits
 * fins » regle le contour de la silhouette, celle-ci les couleurs, une case par couleur. Elle n'existe
 * que s'il y a de quoi cocher (`couleursConcernees`) : le dernier rapport cite des traits trop fins,
 * ou la saisie a deja des couleurs epaissies. Une couleur dont tous les traits sont fins n'a plus de
 * pastille, et reste proposee ici. `id` est celui de la carte, dont le reglage voisin, « Traits fins »,
 * nomme ses radios `${id}-mode`.
 *
 * **Le focus ne se perd pas** (regle de la page, `CLAUDE.app.md`). La ligne s'en va quand il n'y a plus
 * rien a cocher : sous la main de qui decoche la derniere case d'une couleur que le rapport ne cite
 * plus, ou quand un rapport sans traits remplace l'ancien pendant qu'une case a le focus. Il tomberait
 * sur la page (`body`) : le lecteur d'ecran ne dirait plus ou l'on est. Il va au choix courant de
 * « Traits fins », le reglage juste au-dessus : c'est la que Maj+Tab aurait mene, la ligne etant la
 * derniere de la carte, donc Tab continue comme si elle n'avait jamais ete la, et le lecteur d'ecran
 * dit ou l'on est (« Traits fins, Fidele »). Le titre de la carte, destination des liens de l'apercu,
 * ferait perdre sa place a qui est tout en bas des reglages.
 */
function LigneTraitsDeCouleur({
  id,
  concernees,
  epaissies,
  largeur,
  changer,
}: {
  id: string;
  concernees: readonly CouleurConcernee[];
  /** Les couleurs deja cochees : celles de la saisie. */
  epaissies: readonly string[];
  /** La largeur que la regle cite, ecrite : « 0,8 ». */
  largeur: string;
  changer: (couleursEpaissies: string[]) => void;
}) {
  const groupe = useRef<HTMLFieldSetElement>(null);
  // Le nettoyage d'un effet de mise en page tourne avant que la ligne ne quitte le DOM : le focus y est
  // encore. Il n'y va que s'il y etait : React rend de lui-meme le focus a l'element qui l'avait avant
  // un rendu, mais pas a la page (`body`, qu'on ne peut pas focaliser), et le radio s'allumerait pour
  // personne.
  useLayoutEffect(
    () => () => {
      if (!groupe.current?.contains(document.activeElement)) return;
      document
        .querySelector<HTMLInputElement>(`input[type="radio"][name="${id}-mode"]:checked`)
        ?.focus();
    },
    [id],
  );

  return (
    <Ligne
      aide={`plus fins que ${largeur} mm, ils ne sortent pas ; épaissis, ils passent à ${largeur} mm`}
      aideId={`${id}-traits-couleur-aide`}
      idLibelle={`${id}-traits-couleur`}
      libelle="Traits de couleur"
    >
      <fieldset
        aria-describedby={`${id}-traits-couleur-aide`}
        aria-labelledby={`${id}-traits-couleur`}
        className="flex flex-wrap items-center gap-x-4 gap-y-1"
        ref={groupe}
      >
        {concernees.map((couleur) => (
          // 44 px de haut au telephone, pour le pouce : toute l'etiquette coche la case.
          <label
            className="inline-flex min-h-11 cursor-pointer items-center gap-2 text-sm md:min-h-9"
            key={couleur.nom}
          >
            <input
              checked={couleur.epaissie}
              // `scheme-light-dark` : la case suit le theme de la page (`prefers-color-scheme`) comme le
              // reste de la console. Sans lui, la case native reste claire en sombre : un carre blanc,
              // que rien ne distingue de la pastille de teinte du blanc a cote d'elle.
              className="size-4 shrink-0 cursor-pointer accent-primary scheme-light-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
              onChange={(event) =>
                changer(
                  event.target.checked
                    ? [...new Set([...epaissies, couleur.nom])]
                    : epaissies.filter((nom) => nom !== couleur.nom),
                )
              }
              type="checkbox"
            />
            {couleur.hexa ? (
              // Un disque, et non un carre : posee juste apres la case, une pastille carree se lisait
              // comme une seconde case a cocher (relecture de #397, vu en clair comme en sombre).
              <span
                aria-hidden="true"
                className="size-[18px] shrink-0 rounded-full border border-border"
                style={{ backgroundColor: couleur.hexa }}
              />
            ) : null}
            {couleur.nom}
          </label>
        ))}
      </fieldset>
    </Ligne>
  );
}

export function CarteReglages({
  saisie,
  changer,
  rapport,
  avecImage,
  className,
  serreLivres,
}: {
  saisie: Saisie;
  changer: (saisie: Saisie) => void;
  /** Le rapport du dernier rendu reussi, pour les aides qui citent ses chiffres. */
  rapport: Rapport | null;
  avecImage: boolean;
  className?: string;
  serreLivres?: ReglagesSerreLivres | undefined;
}) {
  const id = useId();
  const lecture = lireSaisie(saisie);
  const poser = (partiel: Partial<Saisie>): void => changer({ ...saisie, ...partiel });

  // Un nom vide avant la premiere image n'est pas une faute : rien ne peut partir.
  const nomFautif = lecture.nomInvalide && (avecImage || saisie.nom !== "");
  const premiere = rapport?.couleurs?.[0]?.nom;
  const aideNom =
    premiere && !lecture.nomInvalide
      ? `donne ${saisie.nom}.3mf, ${saisie.nom}-${premiere}.stl…`
      : "les fichiers porteront ce nom";
  const aideHauteur =
    rapport && saisie.attache
      ? `de la silhouette ; ${nombreFr(rapport.hauteur_hors_tout_mm, 1)} mm hors tout avec la barre`
      : "de la silhouette";
  // Les traits de couleur (#397) : de quoi cocher, et la largeur que la regle cite.
  const concernees = couleursConcernees(saisie, rapport);
  const largeurPastille = nombreFr(largeurMinDesPastilles(rapport), 2);

  const reglagesAvances = (
    <>
      <Ligne
        aide={serreLivres ? "toujours la base modulaire : c'est celle du Bookend" : null}
        idLibelle={`${id}-attache`}
        libelle="Attache"
      >
        <Bascule
          changer={(valeur) => poser({ attache: valeur === "oui" })}
          desactivee={serreLivres !== undefined}
          idLibelle={`${id}-attache`}
          options={ATTACHES}
          valeur={saisie.attache ? "oui" : "non"}
        />
      </Ligne>

      <Ligne
        aide="nombre de couleurs : auto, ou de 1 à 8"
        aideId={`${id}-couleurs-aide`}
        idLibelle={`${id}-couleurs`}
        libelle="Couleurs"
      >
        {/* Le groupe porte le libelle « Couleurs » ; la case et la liste ont chacune le leur. */}
        <fieldset
          aria-describedby={`${id}-couleurs-aide`}
          aria-labelledby={`${id}-couleurs`}
          className="flex flex-wrap items-center gap-x-4 gap-y-2"
        >
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
            <input
              checked={saisie.couleursGardees}
              className={cn(
                "relative h-5 w-9 shrink-0 cursor-pointer appearance-none rounded-full bg-muted-foreground transition-colors checked:bg-primary",
                "before:absolute before:top-[3px] before:left-[3px] before:h-3.5 before:w-3.5 before:rounded-full before:bg-card before:transition-transform checked:before:translate-x-4",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card",
              )}
              onChange={(event) => poser({ couleursGardees: event.target.checked })}
              type="checkbox"
            />
            garder
          </label>
          <label className="sr-only" htmlFor={`${id}-nombre`}>
            Nombre de couleurs
          </label>
          <select
            className={cn(CHAMP, "pr-7")}
            disabled={!saisie.couleursGardees}
            id={`${id}-nombre`}
            onChange={(event) =>
              poser({
                nombreCouleurs: event.target.value === "auto" ? "auto" : Number(event.target.value),
              })
            }
            value={String(saisie.nombreCouleurs)}
          >
            {NOMBRES.map((nombre) => (
              <option key={nombre} value={nombre}>
                {nombre}
              </option>
            ))}
          </select>
        </fieldset>
      </Ligne>

      <Ligne
        aide={
          saisie.attache
            ? "Auto : seulement si les pieds s'écartent plus que la barre"
            : "sans attache, pas de ligne de sol"
        }
        aideId={`${id}-sol-aide`}
        idLibelle={`${id}-sol`}
        libelle="Ligne de sol"
      >
        <Bascule
          changer={(sol) => poser({ sol })}
          decritPar={`${id}-sol-aide`}
          desactivee={!saisie.attache}
          idLibelle={`${id}-sol`}
          options={SOLS}
          valeur={saisie.sol}
        />
      </Ligne>

      {/*
       * La hauteur de la ligne, sous sa bascule (#175). Elle disparait sur « Non » : il n'y a
       * alors pas de ligne dont regler la hauteur, et un champ eteint de plus ne dirait rien.
       * Sans attache, elle suit la bascule et s'eteint, pour la meme raison qu'elle.
       */}
      {saisie.sol !== "non" ? (
        <Ligne
          aide="vide = l'outil la relève juste assez pour que les deux pieds posent"
          aideId={`${id}-sol-hauteur-aide`}
          erreur={
            lecture.solHauteurInvalide
              ? `De ${SOL_HAUTEUR_MIN_MM} à ${SOL_HAUTEUR_MAX_MM} mm, ou vide.`
              : null
          }
          libelle="Hauteur du sol"
          pour={`${id}-sol-hauteur`}
        >
          <span className="inline-flex items-center gap-2 text-sm">
            <input
              aria-describedby={`${id}-sol-hauteur-aide`}
              aria-invalid={lecture.solHauteurInvalide || undefined}
              className={cn(CHAMP, "w-20")}
              disabled={!saisie.attache}
              id={`${id}-sol-hauteur`}
              inputMode="decimal"
              max={SOL_HAUTEUR_MAX_MM}
              min={SOL_HAUTEUR_MIN_MM}
              onChange={(event) => poser({ solHauteur: event.target.value })}
              placeholder="auto"
              type="number"
              value={saisie.solHauteur}
            />
            mm
          </span>
        </Ligne>
      ) : null}

      <Ligne
        aide={
          saisie.mode === "fidele"
            ? "Fidèle : le contour reste celui du dessin"
            : "Épaissir : les parties plus fines que 2 mm sont épaissies"
        }
        aideId={`${id}-mode-aide`}
        idLibelle={`${id}-mode`}
        libelle="Traits fins"
      >
        <Bascule
          changer={(mode) => poser({ mode })}
          decritPar={`${id}-mode-aide`}
          idLibelle={`${id}-mode`}
          options={MODES}
          valeur={saisie.mode}
        />
      </Ligne>

      {/* Les traits de couleur (#397), sous « Traits fins » : de quoi cocher, ou rien. */}
      {concernees.length > 0 ? (
        <LigneTraitsDeCouleur
          changer={(couleursEpaissies) => poser({ couleursEpaissies })}
          concernees={concernees}
          epaissies={saisie.couleursEpaissies}
          id={id}
          largeur={largeurPastille}
        />
      ) : null}
    </>
  );

  // Un champ plie qui refuse sa valeur le dit dans la ligne du pli : ferme, il ne dirait pas pourquoi
  // rien ne part.
  const erreurPliee = lecture.solHauteurInvalide
    ? `Hauteur du sol : de ${SOL_HAUTEUR_MIN_MM} à ${SOL_HAUTEUR_MAX_MM} mm, ou vide.`
    : null;

  return (
    <section
      aria-labelledby={`${id}-titre`}
      className={cn(
        serreLivres ? "min-w-0" : "rounded-lg border border-border bg-card p-5",
        className,
      )}
    >
      {serreLivres ? (
        <h3 className="sr-only" id={`${id}-titre`}>
          {serreLivres.titre}
        </h3>
      ) : (
        <h2 className="mb-3 text-[15px] font-semibold" id={`${id}-titre`}>
          Réglages
        </h2>
      )}

      {serreLivres ? (
        serreLivres.ligneNom
      ) : (
        <Ligne
          aide={aideNom}
          aideId={`${id}-nom-aide`}
          erreur={
            nomFautif ? "Minuscules, chiffres et tirets seulement, 40 caractères au plus." : null
          }
          libelle="Nom"
          pour={`${id}-nom`}
        >
          <input
            aria-describedby={`${id}-nom-aide`}
            aria-invalid={nomFautif || undefined}
            autoCapitalize="none"
            autoComplete="off"
            className={cn(CHAMP, "w-full")}
            id={`${id}-nom`}
            maxLength={60}
            onChange={(event) => poser({ nom: event.target.value })}
            placeholder="pris du nom de l'image"
            spellCheck={false}
            type="text"
            value={saisie.nom}
          />
        </Ligne>
      )}

      <Ligne
        aide={serreLivres ? null : aideHauteur}
        aideId={`${id}-hauteur-aide`}
        erreur={lecture.hauteurInvalide ? "De 10 à 400 mm." : null}
        libelle="Hauteur"
        pour={`${id}-hauteur`}
      >
        <span className={cn("inline-flex items-center gap-2 text-sm", serreLivres && "flex-wrap")}>
          <input
            aria-describedby={`${id}-hauteur-aide`}
            aria-invalid={lecture.hauteurInvalide || undefined}
            className={cn(CHAMP, "w-20")}
            id={`${id}-hauteur`}
            inputMode="decimal"
            max={HAUTEUR_MAX_MM}
            min={HAUTEUR_MIN_MM}
            onChange={(event) => poser({ hauteur: event.target.value })}
            type="number"
            value={saisie.hauteur}
          />
          mm
          {serreLivres?.horsTout ? (
            <span className="text-[13px] text-muted-foreground" id={`${id}-hauteur-aide`}>
              {serreLivres.horsTout}
            </span>
          ) : null}
        </span>
      </Ligne>

      {serreLivres ? (
        <>
          {serreLivres.suite}
          <ReglagesPlies erreur={erreurPliee} resume={serreLivres.resume}>
            {reglagesAvances}
          </ReglagesPlies>
        </>
      ) : (
        reglagesAvances
      )}
    </section>
  );
}
